import { authFetch, fetchWithAuth, BASE_BACKEND_URL } from '../../auth'
import { useState, useEffect, useContext, useCallback } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons'
import { Table } from 'react-bootstrap'
import { Box, Typography } from '@mui/material'
import { UserInfoContext } from '../context/UserInfoProvider'
import UserRefundLimitModal from '../component/modal/UserRefundLimitModal'
import LoadingSpinner from '../../components/LoadingSpinner'
import ActionButton from '../../components/action_button/ActionButton'
import { capitalizeFirstLetterFrom, formatMoney } from '../../helpers/utils'
const EMPTY_AMOUNT = '$0.00'

const UserMaxRefundsTab = ({ setFailedRefundLimitUpdate, setSuccessRefundLimitUpdate }) => {
  const { userInfoData } = useContext(UserInfoContext)

  const [isFetching, setIsFetching] = useState(false)
  const [saving, setSaving] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [userRefundData, setUserRefundData] = useState([])
  const [currentMonthData, setCurrentMonthData] = useState(null)
  const [newLimitAmount, setNewLimitAmount] = useState(null)

  useEffect(() => {
    const abortController = new AbortController()

    const handleBeforeUnload = () => abortController.abort('Fetch aborted by user')
    window.addEventListener('beforeunload', handleBeforeUnload)

    fetchUserRefundLimitsFromServer(abortController.signal)

    return () => {
      abortController.abort()
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [userInfoData?.uid])

  useEffect(() => {
    async function fetchAll() {
      await fetchUserRefundLimitsFromServer()
    }
    fetchAll()
  }, [])

  const currentMonthName = () => {
    const today = new Date()
    return today.toLocaleString('es-ES', { month: 'long' })
  }

  const monthAndYearInWords = (date) => {
    let currentDate = new Date(date)
    return `${currentDate.toLocaleString('es-ES', { month: 'long' }).toUpperCase()}, ${currentDate.getFullYear()}`
  }

  const getUsePercentage = (current, used) => {
    return ((used / current) * 100).toString().match(/^-?\d+(?:\.\d{0,2})?/)[0]
  }

  const openRefundLimitModal = () => {
    setShowModal(true)
  }

  const closeRefundLimitModal = (confirmed) => {
    if (confirmed) {
      setSaving(true)
      let jsonData = {
        user_uid: userInfoData.uid,
        user_name: userInfoData.full_name,
        max_refund_amount: Number(newLimitAmount).toFixed(2)
      }
      sendNewLimitToBackend(jsonData)
      setSaving(false)
    }
    setShowModal(false)
  }

  const sendNewLimitToBackend = (body) => {

    authFetch(`${BASE_BACKEND_URL}/user_refund_limits`, {
      method: 'POST',
      body: JSON.stringify(body)
    })
      .then((r) => {
        evaluateResponse(r)
      })
      .catch((e) => console.log(e))
  }

  const evaluateResponse = async (response) => {
    switch (response.status) {
      case 404:
      case 400:
      case 500:
        setFailedRefundLimitUpdate('Error al asignar. Por favor, vuelva a intentarlo.')
        setSaving(false)
        setTimeout(() => setFailedRefundLimitUpdate(false), 10000)
        break
      default:
        setSuccessRefundLimitUpdate(
          userRefundData?.length > 0
            ? `Máximo mensual actualizado para ${userInfoData.uid} – ${userInfoData.full_name}: $${Number(currentMonthData.current_amount).toFixed(2)} → $${Number(newLimitAmount).toFixed(2)}`
            : `Máximo mensual de devoluciones habilitado para ${userInfoData.uid} – ${userInfoData.full_name}.`
        )
        setSaving(false)
        setTimeout(() => setSuccessRefundLimitUpdate(false), 10000)
        fetchUserRefundLimitsFromServer()
        break
    }
  }

  const fetchUserRefundLimitsFromServer = useCallback(async () => {
    //Fetch de data de tabla
    setIsFetching(true)
    const response = await fetchWithAuth(
      `${BASE_BACKEND_URL}/user_refund_limits?user_uid=${userInfoData.uid}`,
      {
        method: 'GET',
      }
    )

    if (response.status === 401) {
      return console.error(response)
    } else {
      setUserRefundData(response['result'])
      if (response['current_month']) setCurrentMonthData(response['current_month'][0])
    }
    setIsFetching(false)
  }, [])

  const refundLimitActionText =
    userRefundData?.length > 0 ? 'Editar máximo mensual' : 'Habilitar máximo mensual'

  return (
    <Box sx={{ pt: 0 }}>
      {showModal && (
        <UserRefundLimitModal
          showModal={true}
          closeModal={closeRefundLimitModal}
          saving={saving}
          isRefundLimitEnabled={userRefundData?.length > 0}
          newLimitAmount={newLimitAmount}
          setNewLimitAmount={setNewLimitAmount}
          currentMonthData={currentMonthData}
        />
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
          borderBottom: '1px solid #E5E7EB',
          pb: 2,
          mb: 2
        }}
      >
        <Typography sx={{ fontSize: 18 }}>Detalle del máximo de devoluciones:</Typography>
        <ActionButton
          text={refundLimitActionText}
          icon={userRefundData?.length > 0 ? 'edit' : 'add_circle'}
          filled
          color="#FF7A30"
          hoverColor="#F06F28"
          disabled={saving || isFetching}
          onClick={openRefundLimitModal}
        />
      </Box>
      {!isFetching && (
        <>
          <div className="assign-modal-info-flex margint-5 mb-16">
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M24 12C24 18.6274 18.6274 24 12 24C5.37258 24 0 18.6274 0 12C0 5.37258 5.37258 0 12 0C18.6274 0 24 5.37258 24 12Z"
                fill="#202020"
              />
              <path
                d="M10.9577 17.6434C10.9577 17.2731 11.088 16.9577 11.3486 16.6971C11.6091 16.4366 11.9246 16.3063 12.2949 16.3063C12.6651 16.3063 12.9806 16.4366 13.2411 16.6971C13.5017 16.9577 13.632 17.2731 13.632 17.6434C13.632 18.0137 13.5017 18.3291 13.2411 18.5897C12.9806 18.8503 12.6651 18.9806 12.2949 18.9806C11.9246 18.9806 11.6091 18.8503 11.3486 18.5897C11.088 18.3291 10.9577 18.0137 10.9577 17.6434ZM13.4674 3.81943C13.4674 6.56229 13.2206 10.1829 12.7269 14.6811H11.8629C11.3829 9.90857 11.1429 6.288 11.1429 3.81943C11.9246 2.96914 12.6994 2.96914 13.4674 3.81943Z"
                fill="white"
              />
            </svg>
            <p className="b-text-price" style={{ margin: 0 }}>
              El límite mensual es recurrente y se reinicia automáticamente el 1 de cada mes.
            </p>
          </div>
          <div className="refund-limit-indicator-background">
            <div className="assign-document-indicator-subtitles">
              <span className="material-symbols-outlined refund-limit-calendar-icon">
                calendar_check
              </span>
              <span className="assign-document-indicator-text">{`Mes: ${capitalizeFirstLetterFrom(currentMonthName())}`}</span>
            </div>
            <div className="assign-document-indicator-subtitles">
              <span className="material-symbols-outlined refund-limit-assigned-icon">
                request_quote
              </span>
              <span className="assign-document-indicator-text">
                {`Monto asignado: ${currentMonthData != null ? formatMoney(currentMonthData.current_amount) : EMPTY_AMOUNT}`}
              </span>
            </div>
            <div className="assign-document-indicator-subtitles">
              <span className="material-symbols-outlined refund-limit-used-icon">data_usage</span>
              <span className="assign-document-indicator-text">
                {`Monto usado: ${currentMonthData != null ? formatMoney(currentMonthData.used_amount) : EMPTY_AMOUNT}`}
              </span>
            </div>
            <div className="assign-document-indicator-subtitles">
              <span className="material-symbols-outlined refund-limit-remaining-icon">
                account_balance_wallet
              </span>
              <span className="assign-document-indicator-text">
                {`Monto restante: ${currentMonthData != null ? formatMoney(currentMonthData.remaining_amount) : EMPTY_AMOUNT}`}
              </span>
            </div>
          </div>
          <div className="mt-25">
            <b>Histórico mensual</b>
          </div>
          <div className="mt-20">
            <Table hover responsive>
              <thead className="table-header-gray-mui">
                <tr>
                  <th className="vertical-align-middle" style={{ padding: '10px 25px' }}>
                    <span className="th-header-text">MES/AÑO</span>
                  </th>
                  <th className="vertical-align-middle">
                    <span className="th-header-text">MONTO ASIGNADO</span>
                  </th>
                  <th className="vertical-align-middle">
                    <span className="th-header-text">MONTO USADO</span>
                  </th>
                  <th className="vertical-align-middle">
                    <span className="th-header-text">MONTO RESTANTE</span>
                  </th>
                  <th
                    className="vertical-align-middle"
                    title="Es el porcentaje del máximo consumido este mes"
                  >
                    <span className="th-header-text"> % DE CUMPLIMIENTO</span>
                    <FontAwesomeIcon icon={faQuestionCircle} className="ml-2" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {userRefundData?.map((month, indx) => (
                  <tr key={indx}>
                    <td className="td-text-limited td-text-limited-space">
                      {monthAndYearInWords(month.created_at)}
                    </td>
                    <td className="td-text-limited">{formatMoney(month.current_amount)}</td>
                    <td className="td-text-limited">{formatMoney(month.used_amount)}</td>
                    <td className="td-text-limited">{formatMoney(month.remaining_amount)}</td>
                    <td className="td-text-limited">
                      {month.current_amount != 0
                        ? `${getUsePercentage(Number(month.current_amount), Number(month.used_amount))} %`
                        : 'N/A'}
                    </td>
                  </tr>
                ))}
                {userRefundData?.length === 0 && (
                  <tr>
                    <td colSpan="5" className="td-text-no-border">
                      Este usuario no tiene habilitado el máximo de devoluciones.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>{' '}
        </>
      )}
      {isFetching ||
        (saving && (
          <div className="updating-screen">
            <div className="loading-card">
              <LoadingSpinner text="Actualizando" size={25} color="#747474" />
            </div>
          </div>
        ))}
    </Box>
  )
}

export default UserMaxRefundsTab
